import { useEffect, useRef, useState } from 'react';

const socials = [
  {
    icon: 'ri-instagram-line',
    name: 'Instagram',
    handle: '@mingzheng_guzheng',
    desc: '日常古箏練習、教學現場片段，以及老師的生活美學',
    color: '#c8607a',
    bg: '#fdf0f3',
    border: '#f5c8d4',
    href: 'https://instagram.com',
    cta: '追蹤 Instagram',
    preview: [
      'https://readdy.ai/api/search-image?query=close%20up%20guzheng%20strings%20with%20warm%20bokeh%20morning%20light%2C%20minimal%20japanese%20aesthetic%2C%20muted%20honey%20tones%2C%20instagram%20square%20format%2C%20lifestyle%20music%20photography&width=300&height=300&seq=ig01&orientation=squarish',
      'https://readdy.ai/api/search-image?query=teacher%20hands%20playing%20guzheng%20in%20cozy%20studio%20warm%20natural%20light%2C%20peaceful%20atmosphere%2C%20earthy%20beige%20tones%2C%20square%20format%20lifestyle%20photography&width=300&height=300&seq=ig02&orientation=squarish',
      'https://readdy.ai/api/search-image?query=guzheng%20detail%20with%20dried%20flowers%20and%20ceramic%20cup%2C%20minimal%20wabi-sabi%20interior%2C%20warm%20cream%20tones%2C%20square%20lifestyle%20flatlay%20photography&width=300&height=300&seq=ig03&orientation=squarish',
    ],
  },
  {
    icon: 'ri-facebook-line',
    name: 'Facebook',
    handle: '鳴箏古箏教學',
    desc: '教學文章、古箏小知識分享，以及最新課程資訊公告',
    color: '#3a6aae',
    bg: '#f0f4fc',
    border: '#c8d8f0',
    href: 'https://facebook.com',
    cta: '追蹤 Facebook',
    preview: [],
  },
  {
    icon: 'ri-line-fill',
    name: 'LINE 官方帳號',
    handle: '@mingzheng',
    desc: '最直接的諮詢管道，加入後可以直接傳訊詢問課程細節',
    color: '#06C755',
    bg: '#f0fbf4',
    border: '#b8eecb',
    href: 'https://line.me',
    cta: '加入 LINE@ 諮詢',
    preview: [],
  },
];

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function SocialSection() {
  const { ref, visible } = useVisible();

  return (
    <section className="py-20 md:py-28 bg-[#f2ede5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px bg-[#b08060]"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">
                  Follow Along
                </span>
              </div>
              <h2
                className="font-serif-tc text-[#2c2420] leading-[1.3]"
                style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
              >
                在社群上<br className="md:hidden" />找到我們
              </h2>
            </div>
            <p className="text-[0.9375rem] leading-[1.75] text-[#7a6e62] max-w-sm">
              不管你現在還在觀望或是已經準備好，歡迎先透過社群認識鳴箏。
            </p>
          </div>

          {/* Social Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {socials.map((s, i) => (
              <div
                key={i}
                className="rounded-2xl border overflow-hidden flex flex-col transition-all duration-700"
                style={{ background: s.bg, borderColor: s.border, transitionDelay: `${i * 0.1}s` }}
              >
                {/* IG Preview Grid */}
                {s.preview.length > 0 && (
                  <div className="grid grid-cols-3 gap-0.5 border-b" style={{ borderColor: s.border }}>
                    {s.preview.map((img, j) => (
                      <div key={j} className="aspect-square overflow-hidden">
                        <img
                          src={img}
                          alt={`${s.name} preview ${j + 1}`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Card Content */}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                      style={{ background: s.color, color: 'white' }}
                    >
                      <i className={`${s.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-[#2c2420] text-[0.9375rem]">{s.name}</p>
                      <p className="text-[0.8125rem] text-[#9a8470]">{s.handle}</p>
                    </div>
                  </div>

                  <p className="text-[0.9375rem] leading-[1.7] text-[#5a4e42] mb-6 flex-1">
                    {s.desc}
                  </p>

                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-white text-[0.875rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap hover:opacity-90"
                    style={{ background: s.color }}
                  >
                    <i className={`${s.icon}`}></i>
                    {s.cta}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
